import type { MenuItemProps, PaperProps, ThemeOptions, WithStyles } from '@material-ui/core';
import { createStyles, Icon, MuiThemeProvider, withStyles } from '@material-ui/core';
import { MenuItem } from '@zinc/ui';
import { createCustomBzTheme } from 'bz-core-controls/styles/mui-next-theme';
import classNames from 'classnames';
import { action, observable } from 'mobx';
import { observer } from 'mobx-react';
import React from 'react';

import Menu from './nesting-menu';

const styles = createStyles({
  subMenuItem: {
    width: '100%'
  },
  caret: {
    marginLeft: 'auto',
    display: 'inline-flex',
    alignItems: 'center'
  }
});

export interface IMenuItem {
  key?: React.Key;
  caption?: string;
  menuItems?: IMenuItem[];
  dataTestId?: string;

  disabled?: MenuItemProps['disabled'];
  onClick?: MenuItemProps['onClick'];
}

interface IProps extends WithStyles<typeof styles>, React.PropsWithChildren {
  caption?: string;
  dataTestId?: string;
  menuItems?: IMenuItem[];
  menuTheme?: ThemeOptions;
  primaryText?: string;
  disabled?: boolean;
  PaperProps?: PaperProps;
  subItemClassName?: string;

  onClick?(e: React.MouseEvent<HTMLElement>): void;
}

@observer
class SubMenuItem extends React.Component<IProps> {
  static defaultProps = {
    onClick: () => null
  };

  @observable menuOpen = false;
  @observable anchorEl?: HTMLElement;

  @action.bound
  setAnchorElement(node: HTMLElement) {
    this.anchorEl = node;
  }

  @action.bound
  handleItemClick(e: React.MouseEvent<HTMLElement>): void {
    const { menuItems, onClick } = this.props;
    if (menuItems && menuItems.length > 0) {
      e.stopPropagation();

      if (!this.anchorEl) {
        this.setAnchorElement(e.currentTarget);
      }
      this.menuOpen = !this.menuOpen;
    } else {
      onClick?.(e);
    }
  }

  @action.bound
  handleSubMenuClose() {
    this.menuOpen = false;
    this.anchorEl = undefined;
  }

  render() {
    const {
      caption,
      children,
      classes,
      dataTestId,
      disabled,
      menuItems,
      menuTheme,
      primaryText,
      PaperProps,
      subItemClassName
    } = this.props;
    const showCaret = !!(menuItems && menuItems.length > 0);

    const theme = createCustomBzTheme(menuTheme);

    return (
      <>
        <MuiThemeProvider theme={theme}>
          <MenuItem
            dataTestId={dataTestId}
            onClick={this.handleItemClick}
            className={classNames(classes.subMenuItem, {
              [`${subItemClassName}`]: !!subItemClassName
            })}
            disabled={disabled}
          >
            {caption || primaryText || (!Array.isArray(children) && children)}
            {showCaret && (
              <span className={classes.caret}>
                <Icon className="fa fa-caret-right" style={{ fontSize: 16, paddingLeft: '10px' }} />
              </span>
            )}
          </MenuItem>
        </MuiThemeProvider>
        {menuItems && menuItems.length > 0 && (
          <Menu
            open={this.menuOpen}
            menuItems={menuItems}
            anchorEl={this.anchorEl}
            onClose={this.handleSubMenuClose}
            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            PaperProps={PaperProps}
          >
            {Array.isArray(children) && children}
          </Menu>
        )}
      </>
    );
  }
}

export default withStyles(styles)(SubMenuItem);
